/**
 * Tasks Page - Kanban board for task management
 * Epic 12: Feladatlista Widget
 */

import {
  PRIORITY_LABEL_KEYS,
  TaskStatus,
  TaskType,
  TYPE_LABEL_KEYS,
  type Task,
  type TaskPriority,
} from '@/api/tasks';
import type { UserResponse } from '@/api/users';
import { Card } from '@/components/ui';
import { useTaskMutations, useTasks, useTaskStats } from '@/hooks/use-tasks';
import { useUsers } from '@/hooks/use-users';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useMemo, useState } from 'react';

// ============================================
// CONFIGURATION
// ============================================

const PRIORITY_CONFIG: Record<TaskPriority, { labelKey: string; color: string; bgColor: string }> =
  {
    0: {
      labelKey: 'status.low',
      color: 'text-muted-foreground',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
    },
    1: {
      labelKey: 'status.medium',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    2: {
      labelKey: 'status.high',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    },
  };

// Kanban columns - only showing active statuses
const KANBAN_STATUSES: TaskStatus[] = [
  TaskStatus.OPEN,
  TaskStatus.IN_PROGRESS,
  TaskStatus.COMPLETED,
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatDate = (dateStr: string | undefined, t: (key: string) => string) => {
  if (!dateStr) return { text: '-', isOverdue: false };

  const date = new Date(dateStr);
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days < 0) return { text: `${Math.abs(days)} ${t('task.daysOverdueLabel')}`, isOverdue: true };
  if (days === 0) return { text: t('task.today'), isOverdue: false };
  if (days === 1) return { text: t('task.tomorrow'), isOverdue: false };
  return { text: date.toLocaleDateString('hu-HU'), isOverdue: false };
};

// ============================================
// COMPONENTS
// ============================================

interface TaskCardProps {
  task: Task;
  userMap: Map<string, UserResponse>;
  onDragStart: (task: Task) => void;
  onSelect: (task: Task) => void;
}

function TaskCard({ task, userMap, onDragStart, onSelect }: TaskCardProps) {
  const { t } = useTranslation();
  const dueInfo = formatDate(task.dueDate, t);
  const priorityConfig = PRIORITY_CONFIG[task.priority] ?? PRIORITY_CONFIG[1];
  const assignedNames = task.assignedToIds
    .map(id => userMap.get(id)?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div
      draggable
      onDragStart={() => onDragStart(task)}
      onClick={() => onSelect(task)}
      className="kgc-card-bg cursor-pointer rounded-lg border border-gray-200 p-4 shadow-sm transition-all hover:shadow-md dark:border-gray-700"
    >
      {/* Priority & Type */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-medium ${priorityConfig.bgColor} ${priorityConfig.color}`}
        >
          {t(priorityConfig.labelKey)}
        </span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          {t(TYPE_LABEL_KEYS[task.type] ?? task.type)}
        </span>
      </div>

      {/* Title */}
      <h4 className="mb-1 font-medium text-gray-900 dark:text-white">{task.title}</h4>
      {task.description && (
        <p className="mb-3 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
          {task.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        {/* Assigned users */}
        <div className="flex items-center gap-2">
          {task.assignedToIds.length > 0 ? (
            <>
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-white">
                {task.assignedToIds.length}
              </div>
              <span
                className="max-w-[120px] truncate text-xs text-gray-500 dark:text-gray-400"
                title={assignedNames}
              >
                {assignedNames || t('task.assignee')}
              </span>
            </>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-300">
              {t('common.noResponsible')}
            </span>
          )}
        </div>

        {/* Due date */}
        <span
          className={`text-xs ${dueInfo.isOverdue ? 'font-medium text-red-600' : 'text-gray-500 dark:text-gray-400'}`}
        >
          {dueInfo.text}
        </span>
      </div>

      {/* Quantity info for shopping tasks */}
      {task.type === TaskType.SHOPPING && task.quantity && (
        <div className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-300">
          {t('task.quantityLabel')}: {task.quantity} {task.unit ?? t('task.unitDefault')}
        </div>
      )}
    </div>
  );
}

interface KanbanColumnProps {
  status: TaskStatus;
  tasks: Task[];
  userMap: Map<string, UserResponse>;
  statusConfig: Record<TaskStatus, { label: string; color: string; bgColor: string }>;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (status: TaskStatus) => void;
  onTaskDragStart: (task: Task) => void;
  onTaskSelect: (task: Task) => void;
}

function KanbanColumn({
  status,
  tasks,
  userMap,
  statusConfig,
  onDragOver,
  onDrop,
  onTaskDragStart,
  onTaskSelect,
}: KanbanColumnProps) {
  const { t } = useTranslation();
  const config = statusConfig[status];

  return (
    <div
      onDragOver={onDragOver}
      onDrop={() => onDrop(status)}
      className="flex min-h-[500px] w-80 flex-shrink-0 flex-col rounded-lg bg-gray-50 dark:bg-gray-800/50"
    >
      {/* Column header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${config.bgColor}`} />
          <h3 className={`font-medium ${config.color}`}>{config.label}</h3>
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            {tasks.length}
          </span>
        </div>
      </div>

      {/* Tasks */}
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {tasks.map(task => (
          <TaskCard
            key={task.id}
            task={task}
            userMap={userMap}
            onDragStart={onTaskDragStart}
            onSelect={onTaskSelect}
          />
        ))}
        {tasks.length === 0 && (
          <div className="flex h-32 items-center justify-center text-sm text-gray-400 dark:text-gray-300">
            {t('task.noTasks')}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE
// ============================================

export function TasksPage() {
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);

  const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bgColor: string }> = {
    [TaskStatus.OPEN]: {
      label: t('status.open'),
      color: 'text-gray-600 dark:text-gray-400',
      bgColor: 'bg-gray-100 dark:bg-gray-800',
    },
    [TaskStatus.IN_PROGRESS]: {
      label: t('status.inProgress'),
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    [TaskStatus.COMPLETED]: {
      label: t('status.completed'),
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
    [TaskStatus.CANCELLED]: {
      label: t('status.interrupted'),
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
    },
  };
  // API hooks
  const [filters] = useState<{
    type?: TaskType;
    search?: string;
    assignedToId?: string;
  }>({});

  const { tasks, isLoading, error, refetch } = useTasks(filters);
  const { stats } = useTaskStats();
  const mutations = useTaskMutations();
  const { users } = useUsers({ status: 'ACTIVE', limit: 100 });
  const userMap = useMemo(() => new Map(users.map(u => [u.id, u])), [users]);

  // Local state
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<TaskType | 'ALL'>('ALL');
  const [filterPriority, setFilterPriority] = useState<TaskPriority | 'ALL'>('ALL');

  // Assign state
  const [assignSearch, setAssignSearch] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: TaskType.TODO as TaskType,
    priority: 1 as TaskPriority,
    dueDate: '',
    quantity: '',
    unit: '',
    assignedToIds: [] as string[],
  });

  const resetForm = () => {
    setAssignSearch('');
    setShowAssignDropdown(false);
    setFormData({
      title: '',
      description: '',
      type: TaskType.TODO,
      priority: 1,
      dueDate: '',
      quantity: '',
      assignedToIds: [],
      unit: '',
    });
  };

  // Filter tasks locally for instant feedback
  const filteredTasks = useMemo(() => {
    const safeTasks = tasks ?? [];
    return safeTasks.filter(task => {
      if (filterType !== 'ALL' && task.type !== filterType) return false;
      if (filterPriority !== 'ALL' && task.priority !== filterPriority) return false;
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase()))
        return false;
      return true;
    });
  }, [tasks, filterType, filterPriority, searchQuery]);

  const getTasksByStatus = (status: TaskStatus) => filteredTasks.filter(t => t.status === status);

  // Drag and drop handlers
  const handleDragStart = (task: Task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (status: TaskStatus) => {
    if (draggedTask && draggedTask.status !== status) {
      try {
        await mutations.changeStatus(draggedTask.id, status);
        void refetch();
      } catch (err) {
        console.error('Failed to change task status:', err);
      }
    }
    setDraggedTask(null);
  };

  // Assign/unassign handlers
  const handleAssignUser = useCallback(
    async (taskId: string, userId: string) => {
      try {
        await mutations.assign(taskId, [userId]);
        if (selectedTask?.id === taskId) {
          setSelectedTask({
            ...selectedTask,
            assignedToIds: [...selectedTask.assignedToIds, userId],
          });
        }
        void refetch();
      } catch (err) {
        console.error('Failed to assign user:', err);
      }
      setAssignSearch('');
      setShowAssignDropdown(false);
    },
    [mutations, selectedTask, refetch]
  );

  const handleUnassignUser = useCallback(
    async (taskId: string, userId: string) => {
      try {
        await mutations.unassign(taskId, [userId]);
        if (selectedTask?.id === taskId) {
          setSelectedTask({
            ...selectedTask,
            assignedToIds: selectedTask.assignedToIds.filter(id => id !== userId),
          });
        }
        void refetch();
      } catch (err) {
        console.error('Failed to unassign user:', err);
      }
    },
    [mutations, selectedTask, refetch]
  );

  const filteredUsers = useMemo(() => {
    if (!assignSearch.trim()) return users;
    const q = assignSearch.toLowerCase();
    return users.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, assignSearch]);

  // Create task handler
  const handleCreateTask = async () => {
    if (!formData.title.trim()) return;

    try {
      const created = await mutations.create({
        locationId: user?.tenantId ?? '',
        type: formData.type,
        title: formData.title,
        description: formData.description || undefined,
        priority: formData.priority,
        dueDate: formData.dueDate || undefined,
        quantity: formData.quantity ? parseInt(formData.quantity, 10) : undefined,
        unit: formData.unit || undefined,
      });
      if (formData.assignedToIds.length > 0) {
        await mutations.assign(created.id, formData.assignedToIds);
      }
      setShowNewTaskModal(false);
      resetForm();
      void refetch();
    } catch (err) {
      console.error('Failed to create task:', err);
    }
  };

  // Status change in modal
  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      await mutations.changeStatus(taskId, newStatus);
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, status: newStatus });
      }
      void refetch();
    } catch (err) {
      console.error('Failed to change status:', err);
    }
  };

  // Calculate stats
  const displayStats = useMemo(() => {
    if (stats) {
      return {
        open: stats.byStatus[TaskStatus.OPEN] ?? 0,
        inProgress: stats.byStatus[TaskStatus.IN_PROGRESS] ?? 0,
        completed: stats.byStatus[TaskStatus.COMPLETED] ?? 0,
        total: Object.values(stats.byStatus).reduce((a, b) => a + b, 0),
      };
    }
    return {
      open: filteredTasks.filter(t => t.status === TaskStatus.OPEN).length,
      inProgress: filteredTasks.filter(t => t.status === TaskStatus.IN_PROGRESS).length,
      completed: filteredTasks.filter(t => t.status === TaskStatus.COMPLETED).length,
      total: filteredTasks.length,
    };
  }, [stats, filteredTasks]);

  return (
    <div className="kgc-bg min-h-screen">
      {/* Header */}
      <header className="kgc-card-bg border-b border-gray-200 dark:border-gray-700">
        <div className="mx-auto flex max-w-full items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('task.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('task.kanbanTitle')}</p>
          </div>
          <button
            onClick={() => setShowNewTaskModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('task.newTask')}
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="border-b border-gray-200 bg-white px-6 py-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 sm:max-w-xs">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-[#99f6e4]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            />
          </div>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value as TaskType | 'ALL')}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="ALL">{t('page.allTypes')}</option>
            {Object.values(TaskType).map(type => (
              <option key={type} value={type}>
                {t(TYPE_LABEL_KEYS[type] ?? type)}
              </option>
            ))}
          </select>

          {/* Priority filter */}
          <select
            value={filterPriority === 'ALL' ? 'ALL' : String(filterPriority)}
            onChange={e => {
              const val = e.target.value;
              setFilterPriority(val === 'ALL' ? 'ALL' : (parseInt(val, 10) as TaskPriority));
            }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="ALL">{t('task.allPriorities')}</option>
            {([0, 1, 2] as TaskPriority[]).map(priority => (
              <option key={priority} value={priority}>
                {t(PRIORITY_LABEL_KEYS[priority] ?? priority)}
              </option>
            ))}
          </select>

          {/* Stats */}
          <div className="ml-auto flex items-center gap-4 text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">
                {displayStats.total}
              </span>{' '}
              {t('task.taskCount')}
            </span>
            <span className="text-orange-600">
              <span className="font-medium">
                {filteredTasks.filter(t => t.priority === 2).length}
              </span>{' '}
              {t('task.highPriority')}
            </span>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center p-12">
          <div className="text-center text-gray-500 dark:text-gray-400">{t('task.loading')}</div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-6">
          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
            <div className="text-center text-red-600 dark:text-red-400">
              {t('common.error')}: {error}
            </div>
          </Card>
        </div>
      )}

      {/* Kanban Board */}
      {!isLoading && !error && (
        <div className="overflow-x-auto p-6">
          <div className="flex gap-6">
            {KANBAN_STATUSES.map(status => (
              <KanbanColumn
                key={status}
                status={status}
                tasks={getTasksByStatus(status)}
                userMap={userMap}
                statusConfig={STATUS_CONFIG}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onTaskDragStart={handleDragStart}
                onTaskSelect={setSelectedTask}
              />
            ))}
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="kgc-card-bg max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl shadow-xl">
            {/* Modal header */}
            <div className="flex items-start justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CONFIG[selectedTask.status].bgColor} ${STATUS_CONFIG[selectedTask.status].color}`}
                  >
                    {STATUS_CONFIG[selectedTask.status].label}
                  </span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${PRIORITY_CONFIG[selectedTask.priority]?.bgColor ?? ''} ${PRIORITY_CONFIG[selectedTask.priority]?.color ?? ''}`}
                  >
                    {t(PRIORITY_CONFIG[selectedTask.priority]?.labelKey ?? 'status.medium')}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedTask.title}
                </h2>
              </div>
              <button
                onClick={() => {
                  setSelectedTask(null);
                  setAssignSearch('');
                  setShowAssignDropdown(false);
                }}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted dark:hover:text-muted-foreground"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="p-6">
              {selectedTask.description && (
                <div className="mb-6">
                  <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('task.descriptionLabel')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">{selectedTask.description}</p>
                </div>
              )}

              <div className="mb-6 grid grid-cols-2 gap-4">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common.type')}
                  </h3>
                  <span className="text-gray-900 dark:text-white">
                    {t(TYPE_LABEL_KEYS[selectedTask.type] ?? selectedTask.type)}
                  </span>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.deadline')}
                  </h3>
                  <p
                    className={`${formatDate(selectedTask.dueDate, t).isOverdue ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}
                  >
                    {selectedTask.dueDate
                      ? new Date(selectedTask.dueDate).toLocaleDateString('hu-HU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '-'}
                  </p>
                </div>
              </div>

              {selectedTask.type === TaskType.SHOPPING && selectedTask.quantity && (
                <div className="mb-6">
                  <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('task.quantityLabel')}
                  </h3>
                  <p className="text-gray-900 dark:text-white">
                    {selectedTask.quantity} {selectedTask.unit ?? t('task.unitDefault')}
                  </p>
                </div>
              )}

              {/* Assign users */}
              <div className="mb-6">
                <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('task.assignedUsers')}
                </h3>
                {/* Current assignees */}
                <div className="mb-2 flex flex-wrap gap-2">
                  {selectedTask.assignedToIds.map(uid => {
                    const u = userMap.get(uid);
                    return (
                      <span
                        key={uid}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                      >
                        {u?.name ?? uid}
                        <button
                          onClick={() => handleUnassignUser(selectedTask.id, uid)}
                          className="ml-1 text-primary/60 hover:text-primary"
                          title={t('task.removeAssignee')}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                  {selectedTask.assignedToIds.length === 0 && (
                    <span className="text-sm text-muted-foreground">
                      {t('common.noResponsible')}
                    </span>
                  )}
                </div>
                {/* Add assignee */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('task.searchUsers')}
                    value={assignSearch}
                    onChange={e => {
                      setAssignSearch(e.target.value);
                      setShowAssignDropdown(true);
                    }}
                    onFocus={() => setShowAssignDropdown(true)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  />
                  {showAssignDropdown && (
                    <div className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                      {filteredUsers
                        .filter(u => !selectedTask.assignedToIds.includes(u.id))
                        .map(u => (
                          <button
                            key={u.id}
                            onClick={() => handleAssignUser(selectedTask.id, u.id)}
                            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-white">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div>{u.name}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </button>
                        ))}
                      {filteredUsers.filter(u => !selectedTask.assignedToIds.includes(u.id))
                        .length === 0 && (
                        <div className="px-4 py-2 text-sm text-muted-foreground">—</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick status change */}
              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('task.statusChange')}
                </h3>
                <div className="flex gap-2">
                  {KANBAN_STATUSES.map(status => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(selectedTask.id, status)}
                      disabled={mutations.isLoading}
                      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                        selectedTask.status === status
                          ? `${STATUS_CONFIG[status].bgColor} ${STATUS_CONFIG[status].color}`
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                      }`}
                    >
                      {STATUS_CONFIG[status].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
              <button
                onClick={() => {
                  setSelectedTask(null);
                  setAssignSearch('');
                  setShowAssignDropdown(false);
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-muted"
              >
                {t('task.closeButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="kgc-card-bg w-full max-w-lg rounded-xl shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('task.newTask')}
              </h2>
              <button
                onClick={() => {
                  setShowNewTaskModal(false);
                  resetForm();
                }}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted dark:hover:text-muted-foreground"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('task.titleLabel')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  placeholder={t('task.titlePlaceholder')}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('task.descriptionLabel')}
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  placeholder={t('task.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('common.type')}
                  </label>
                  <select
                    value={formData.type}
                    onChange={e => setFormData({ ...formData, type: e.target.value as TaskType })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {Object.values(TaskType).map(type => (
                      <option key={type} value={type}>
                        {t(TYPE_LABEL_KEYS[type] ?? type)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('task.priorityLabel')}
                  </label>
                  <select
                    value={formData.priority}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        priority: parseInt(e.target.value, 10) as TaskPriority,
                      })
                    }
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {([0, 1, 2] as TaskPriority[]).map(priority => (
                      <option key={priority} value={priority}>
                        {t(PRIORITY_LABEL_KEYS[priority] ?? priority)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('service.deadline')}
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>

              {/* Quantity fields for shopping tasks */}
              {formData.type === TaskType.SHOPPING && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('task.quantityFieldLabel')}
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={e => setFormData({ ...formData, quantity: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('task.unitLabel')}
                    </label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={e => setFormData({ ...formData, unit: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                      placeholder="db"
                    />
                  </div>
                </div>
              )}

              {/* Assign users */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('task.assignTo')}
                </label>
                {/* Selected assignees */}
                {formData.assignedToIds.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {formData.assignedToIds.map(uid => {
                      const u = userMap.get(uid);
                      return (
                        <span
                          key={uid}
                          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                        >
                          {u?.name ?? uid}
                          <button
                            onClick={() =>
                              setFormData({
                                ...formData,
                                assignedToIds: formData.assignedToIds.filter(id => id !== uid),
                              })
                            }
                            className="ml-1 text-primary/60 hover:text-primary"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* User search dropdown */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('task.searchUsers')}
                    value={assignSearch}
                    onChange={e => {
                      setAssignSearch(e.target.value);
                      setShowAssignDropdown(true);
                    }}
                    onFocus={() => setShowAssignDropdown(true)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  />
                  {showAssignDropdown && (
                    <div className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                      {filteredUsers
                        .filter(u => !formData.assignedToIds.includes(u.id))
                        .map(u => (
                          <button
                            key={u.id}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                assignedToIds: [...formData.assignedToIds, u.id],
                              });
                              setAssignSearch('');
                              setShowAssignDropdown(false);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-white">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div>{u.name}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowNewTaskModal(false);
                  resetForm();
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-muted"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateTask}
                disabled={mutations.isLoading || !formData.title.trim()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {t('task.createButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
